import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import SocketService from '@src/services/SocketService';

import { usePeersStore, useRoomStore } from '@src/stores';

import { ECommonSocketKeys, EPeerPermission } from '@src/types';

interface FetchPeersPermissionsResponse {
  success: boolean;
  data: Record<string, string[]>;
}

/**
 * Hook for handling peer permissions socket events
 * Listens for peersPermissionsUpdated event and fetches peer permissions
 */
const usePeerPermissionsSocketListener = ({ isConnected }: { isConnected: boolean }) => {
  const { setShouldRefetchPeerPermissions } = usePeersStore(
    useShallow((state) => ({
      setShouldRefetchPeerPermissions: state.setShouldRefetchPeerPermissions,
    }))
  );

  useEffect(() => {
    if (!isConnected) return;

    const handlePeersPermissionsUpdated = async (data: { eventSource: string; message: string }) => {
      try {
        // Directly fetch peer permissions when the event is received
        const response: FetchPeersPermissionsResponse = await SocketService.request(
          ECommonSocketKeys.FETCH_PEERS_PERMISSIONS,
          {}
        );

        if (response?.success) {
          // Keep local annotate permission in sync with the backend's authoritative permissions map.
          // It's better to make sure the peer id is in the response data with the correct permissions type, not just dwell on success response
          const myPeerId = SocketService.getSocketId() ?? '';
          const myPerms = (myPeerId && response?.data?.[myPeerId]) || [];
          const hasAnnotate = Array.isArray(myPerms) && myPerms.includes(EPeerPermission.MAGIC_PEN_ALLOW_ANNOTATE);
          useRoomStore.getState().setHasMagicPenAnnotatePermission(hasAnnotate);

          // Set the flag to trigger UI updates if needed
          setShouldRefetchPeerPermissions(true);
        }
      } catch (error) {
        console.error('Failed to fetch peers permissions:', error);
      }
    };

    SocketService.on(ECommonSocketKeys.PEERS_PERMISSIONS_UPDATED, handlePeersPermissionsUpdated);
    return () => {
      SocketService.off(ECommonSocketKeys.PEERS_PERMISSIONS_UPDATED, handlePeersPermissionsUpdated);
    };
  }, [isConnected, setShouldRefetchPeerPermissions]);
};

export default usePeerPermissionsSocketListener;
